// shared.module.ts - Refactored for Modularity

// ---------------------------------------------------------------------
// 1. ANGULAR CORE IMPORTS
// ---------------------------------------------------------------------
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

// ---------------------------------------------------------------------
// 2. SHARED COMMON MODULE (Directives, Pipes, Basic Utilities)
// ---------------------------------------------------------------------
import { SharedCommonModule } from './shared-common.module';
import { SharedPopupsModule } from './shared-popups.module';

// ---------------------------------------------------------------------
// 3. THIRD PARTY LIBRARIES
// ---------------------------------------------------------------------
import { NgbDropdown, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { FlatpickrModule } from 'angularx-flatpickr';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
// import { CountdownComponent, CountdownModule } from 'ngx-countdown';
// import { ImageCropperComponent } from 'ngx-image-cropper';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { QuillModule } from 'ngx-quill';
// import { SocketIoModule } from 'ngx-socket-io';
import { ToastrModule } from 'ngx-toastr';
// NgApexchartsModule and HighchartsChartModule moved to specific modules (dashboard, owner-earnings)
// to reduce initial bundle size

// import { NgxChartsModule } from '@swimlane/ngx-charts';
// import { GalleryModule } from 'ng-gallery';
// import { LightboxModule } from 'ng-gallery/lightbox';

import { AngularSvgIconModule } from 'angular-svg-icon';
import { NgOtpInputModule } from 'ng-otp-input';

import { MarkdownModule } from 'ngx-markdown';
import { NgxSkeletonModule } from 'ngx-skeleton';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { MediaLibraryModule } from '../features/media-library/media-library.module';
import { AiModule } from '../features/ai/ai.module';
import {
  LucideAngularModule,
  Pencil,
  Upload,
  ShieldCheck,
  Check,
  X,
  Zap,
  ArrowRight,
  CheckCircle,
  MessageSquareText,
  MessageSquare,
  KeyRound,
  Info,
  XSquare,
  CreditCard,
  Plus,
  MoreVertical,
  Trash2,
  Calendar,
  AlertCircle,
  Mail,
  HelpCircle,
  MoreHorizontal,
  PlayCircle,
  ArrowLeftRight,
  Settings2,
  UserCheck,
  User,
  Phone,
  Layers,
  CalendarClock,
  Hash,
  Users,
  MousePointer2,
  Clock,
  TrendingUp,
  Globe,
  FileText,
  Link,
  Monitor,
  Smartphone,
  Tablet,
  Search,
  Share2,
  ExternalLink,
  Eye,
  Activity,
  TrendingDown,
  Navigation,
  ChevronDown,
  Lock,
  ArrowLeft,
} from 'lucide-angular';

// ---------------------------------------------------------------------
// 6. CONSTANT GROUPINGS
// ---------------------------------------------------------------------
// Note: Directives and pipes are now in SharedCommonModule

const UI_LIBRARIES: any[] = [NgxSkeletonModule]; // [NgxSkeletonLoaderModule];

// Popups
const POPUPS = [];

// All declarations for this module (only components, directives/pipes are in SharedCommonModule)
const ALL_SHARED_DECLARATIONS: any[] = [...POPUPS];

// ---------------------------------------------------------------------
// 7. MODULE DEFINITION
// ---------------------------------------------------------------------
@NgModule({
  declarations: ALL_SHARED_DECLARATIONS,
  imports: [
    // Import SharedCommonModule for directives, pipes, and basic utilities
    SharedCommonModule,
    SharedPopupsModule,

    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,

    // Third-party Libraries
    NgbModule,
    // NgxChartsModule,
    // CountdownModule,
    // SocketIoModule,
    BsDatepickerModule.forRoot(),
    FlatpickrModule.forRoot(),
    InfiniteScrollModule,
    NgSelectModule,
    QuillModule.forRoot(),
    // NgApexchartsModule - moved to dashboard.module and owner-earnings.module
    MarkdownModule.forRoot(),
    NgxTippyModule,
    ToastrModule.forRoot({
      positionClass: 'toast-bottom-right',
      preventDuplicates: true,
    }),
    // HighchartsChartModule - moved to owner-earnings.module
    // GalleryModule.withConfig({}),
    // LightboxModule,
    NgOtpInputModule,
    AngularSvgIconModule.forRoot(),
    /* NgxSkeletonLoaderModule.forRoot({
      animation: 'pulse',
      loadingText: 'This item is actually loading...',
    }), */
    // NgxIntlTelInputModule,
    // ...UI_LIBRARIES,
    // ImageCropperComponent,
    MediaLibraryModule,
    AiModule,
    LucideAngularModule.pick({
      Pencil,
      Upload,
      ShieldCheck,
      Check,
      X,
      Zap,
      ArrowRight,
      CheckCircle,
      MessageSquareText,
      MessageSquare,
      KeyRound,
      Info,
      XSquare,
      CreditCard,
      Plus,
      MoreVertical,
      Trash2,
      Calendar,
      AlertCircle,
      Mail,
      HelpCircle,
      // Added for CurrentPlanComponent
      MoreHorizontal,
      PlayCircle,
      ArrowLeftRight,
      Settings2,
      UserCheck,
      User,
      Phone,
      Layers,
      CalendarClock,
      Hash,
      Users,
      MousePointer2,
      Clock,
      TrendingUp,
      Globe,
      FileText,
      Link,
      Monitor,
      Smartphone,
      Tablet,
      Search,
      Share2,
      ExternalLink,
      Eye,
      Activity,
      TrendingDown,
      Navigation,
      ChevronDown,
      Lock,
      ArrowLeft,
    }),
  ],
  exports: [
    // Export SharedCommonModule (includes directives, pipes, and common modules)
    SharedCommonModule,
    SharedPopupsModule,
    ...ALL_SHARED_DECLARATIONS,

    // Angular & Third-party modules
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgbModule,
    // NgxChartsModule,
    // CountdownModule,
    // SocketIoModule,
    BsDatepickerModule,
    FlatpickrModule,
    InfiniteScrollModule,
    NgSelectModule,
    QuillModule,
    MarkdownModule,
    // NgApexchartsModule - now in dashboard.module and owner-earnings.module
    ToastrModule,
    // HighchartsChartModule - now in owner-earnings.module
    // GalleryModule,
    // LightboxModule,
    NgOtpInputModule,
    AngularSvgIconModule,
    // NgxIntlTelInputModule,
    NgxTippyModule,
    // ...UI_LIBRARIES,
    // ImageCropperComponent,
    MediaLibraryModule,
    AiModule,
    LucideAngularModule,
  ],
  providers: [NgbDropdown],
})
export class SharedModule {}
